import React, { useCallback, useEffect } from 'react';
import SharedTable from 'shared/ui/Table';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import { withBreakpoints } from 'react-breakpoints';
import {
  selectIsFetching, selectShowDetail, selectVulns,
  selectSelected,
  selectShowContextMenu
} from 'store/Contextualization/AssetDetail/selectors';
import { selectCustomAttributes } from 'store/CustomAttributes/selectors';
import { selectVulnsAssetFields } from 'store/Preferences/vulnsAsset/selectors';
import { setFieldWidth } from 'store/Preferences/vulnsGeneral/actions';
import { PropTypes } from 'prop-types';
import manageVulnsSchema from 'Screens/Contextualization/Manage/components/ManageTable/components/Table/schema';
import { handleResizedChange, getThProps, getTrProps, findSelectedVuln } from './Functions';
import { setOrderByVulnsInHost } from 'store/Host/actions';
import { selectVulnRow, showContextMenu, unSelectAll } from 'store/Contextualization/AssetDetail/actions';
import { selectRiskScoreFeatureFlag } from 'store/Faraday/selectors';
import useTheme from 'Hooks/UseTheme';
import { selectShowQuickPlanner } from 'store/Manage/selectors';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_TAGS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';

const Table = ({ currentBreakpoint }) => {
  const { themeStyles } = useTheme();
  const dispatch = useDispatch();
  const customFields = useSelector(selectCustomAttributes);
  const showVulnDetail = useSelector((state) => selectShowDetail('vulns', state));
  const vulnsSelected = useSelector((state) => selectSelected('vulns', state));
  const isFetching = useSelector((state) => selectIsFetching('vulns', state));
  const data = useSelector(selectVulns);
  const fields = useSelector(selectVulnsAssetFields);
  const isAllowedByLicense = useSelector(selectRiskScoreFeatureFlag);
  const isTagsAllowedByLicense = useSelector((state) => selectAllowedByRole(state, FEATURE_TAGS, PERMISSION_TYPES.READ));

  const isDisplayingContextMenu = useSelector(selectShowContextMenu);
  const noDataText = isFetching ? '' : <FormattedMessage id="workspaces.table.noData" />;
  const showQuickPlanner = useSelector(selectShowQuickPlanner);
  const context = {
    isPivot: true,
    customFields,
    showVulnDetail,
    currentBreakpoint,
    groupBy: false,
    fields,
    isFetching,
    isFetchingExpandedData: false,
    hasGroupedData: false,
    isAllowedByLicense,
    isTagsAllowedByLicense,
    vulnsAssetTable: true,
    showQuickPlanner,
    theme: themeStyles
  };

  const selectRowAction = (row) => dispatch(selectVulnRow({}, row));
  const unSelectAllAction = (row) => dispatch(unSelectAll('vulns'));
  const setFieldWidthAction = (field, value) => dispatch(setFieldWidth(field, value, 'manage_table'));

  const handleResizeColumn = useCallback(
    debounce((newResized) => {
      handleResizedChange(newResized, fields, setFieldWidthAction);
    }, 300),
    [fields]
  );

  const showContextMenuAction = (value, xPos, yPos) => dispatch(showContextMenu(value, xPos, yPos));

  const fetchData = (sorted) => !isFetching && dispatch(setOrderByVulnsInHost(sorted));

  useEffect(() => {
    return () => {
      handleResizeColumn.cancel();
    };
  }, [handleResizeColumn]);

  return (
    <SharedTable
      schema={ manageVulnsSchema }
      context={ context }
      data={ data }
      isLoading={ isFetching }
      onFetch={ fetchData }
      getRowProps={ (rowInfo) => getTrProps(rowInfo, vulnsSelected, selectRowAction, unSelectAllAction, isFetching, showContextMenuAction) }
      isRowSelected={ (rowInfo) => !!findSelectedVuln(rowInfo, vulnsSelected) }
      getHeaderProps={ (column) => getThProps(column, fields, currentBreakpoint, showVulnDetail, showContextMenuAction, isDisplayingContextMenu) }
      onResize={ handleResizeColumn }
      noDataText={ noDataText }
    />
  );
};

Table.propTypes = {
  currentBreakpoint: PropTypes.string.isRequired
};

export default withBreakpoints(Table);
